import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { DialogService } from "src/app/services/dialog.service";
import { SessionService } from "src/app/services/session.service";
import { FieldOption, WorkflowService } from "../../service/workflow.service";

interface TriggerTypeOption {
  id: "schedule" | "event" | "manual";
  title: string;
  icon: string;
  subtitle: string;
}

@Component({
  selector: "app-workflow-action-dialog",
  templateUrl: "./workflow-action-dialog.component.html",
  styleUrls: ["./workflow-action-dialog.component.scss"],
})
export class WorkflowActionDialogComponent implements OnInit, OnDestroy {
  workflowgroup: FormGroup = this.fb.group({});
  submitting = false;

  private reqSubcription: Subscription[] = [];

  triggerTypes: TriggerTypeOption[] = [
    { id: "schedule", title: "Schedule", icon: "🕐", subtitle: "Runs on a recurring schedule" },
    { id: "event",    title: "Event",    icon: "⚡", subtitle: "Runs on a patient journey event" },
    { id: "manual",   title: "Manual",   icon: "👆", subtitle: "User initiates the flow" },
  ];

  eventTypes: FieldOption[] = [];

  frequencies: string[] = ["Daily", "Weekly", "Monthly", "Hourly", "Custom"];
  timezones: string[] = ["UTC", "EST", "PST", "IST", "CET"];

  // 48 half-hour slots, 12 AM through 11:30 PM. Stored value is "HH:mm" (24h)
  // so the existing API contract stays intact; only the visible label is 12h.
  frequencyTimeOptions: { value: string; label: string }[] = [];

  get dialogTitle(): string {
    return "Create Workflow";
  }

  get triggerTypeValue(): string {
    return this.workflowgroup.get("trigger_type")?.value || "";
  }

  get isSchedule(): boolean {
    return this.triggerTypeValue === "schedule";
  }

  get isEvent(): boolean {
    return this.triggerTypeValue === "event";
  }

  get isManual(): boolean {
    return this.triggerTypeValue === "manual";
  }

  get canSubmit(): boolean {
    return this.workflowgroup.valid && !this.submitting;
  }

  constructor(
    public dialogRef: MatDialogRef<WorkflowActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private workflowService: WorkflowService,
    private dialogService: DialogService,
    private sessionService: SessionService
  ) { }

  ngOnInit(): void {
    this.frequencyTimeOptions = this.buildFrequencyTimeOptions();

    const eventTypesSub = this.workflowService.getEventTypes().subscribe((types) => {
      this.eventTypes = types;
    });
    this.reqSubcription.push(eventTypesSub);

    const initialTrigger: string =
      this.data?.type === "schedule" ? "schedule" :
      this.data?.type === "manual"   ? "manual" :
      this.data?.type === "event"    ? "event" :
      this.data?.type === "trigger"  ? "event" : "";

    this.workflowgroup = this.fb.group({
      wf_name: ["", Validators.required],
      description: [""],
      trigger_type: [initialTrigger, Validators.required],
      event_type_key: [""],
      frequency: [""],
      time: [""],
      timezone: ["UTC"],
    });

    this.applyConditionalValidators(initialTrigger);

    const typeSub = this.workflowgroup
      .get("trigger_type")!
      .valueChanges.subscribe((t: string) => {
        this.applyConditionalValidators(t);
      });
    this.reqSubcription.push(typeSub);
  }

  ngOnDestroy(): void {
    this.reqSubcription.forEach((s) => s.unsubscribe());
  }

  private applyConditionalValidators(triggerType: string): void {
    const eventCtrl = this.workflowgroup.get("event_type_key");
    const freqCtrl = this.workflowgroup.get("frequency");
    if (!eventCtrl || !freqCtrl) return;

    if (triggerType === "event") {
      eventCtrl.setValidators([Validators.required]);
      freqCtrl.clearValidators();
      freqCtrl.setValue("", { emitEvent: false });
    } else if (triggerType === "schedule") {
      freqCtrl.setValidators([Validators.required]);
      eventCtrl.clearValidators();
      eventCtrl.setValue("", { emitEvent: false });
    } else {
      eventCtrl.clearValidators();
      freqCtrl.clearValidators();
      eventCtrl.setValue("", { emitEvent: false });
      freqCtrl.setValue("", { emitEvent: false });
    }

    eventCtrl.updateValueAndValidity({ emitEvent: false });
    freqCtrl.updateValueAndValidity({ emitEvent: false });
  }

  selectTrigger(id: string): void {
    this.workflowgroup.get("trigger_type")?.setValue(id);
  }

  private buildFrequencyTimeOptions(): { value: string; label: string }[] {
    const opts: { value: string; label: string }[] = [];
    for (let h = 0; h < 24; h++) {
      for (const m of [0, 30]) {
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        const value = `${hh}:${mm}`;
        const period = h < 12 ? "AM" : "PM";
        const display = `${((h + 11) % 12) + 1}:${mm} ${period}`;
        opts.push({ value, label: display });
      }
    }
    return opts;
  }

  onSave(): void {
    if (this.workflowgroup.invalid || this.submitting) return;

    this.submitting = true;
    const v = this.workflowgroup.value;

    const triggerConfig =
      v.trigger_type === "event"
        ? { event_type_key: v.event_type_key }
        : v.trigger_type === "schedule"
        ? { frequency: v.frequency, time: v.time, timezone: v.timezone }
        : {};

    // Keep the create payload minimal — only fields the backend accepted
    // before this change set. Trigger metadata is forwarded via saveFull
    // (which writes the design) and preserved client-side on `def` so the
    // designer navigation has everything it needs.
    const payload = {
      wf_name: v.wf_name,
      description: v.description || "",
      created_by: this.sessionService.getUsername() || "system",
    };

    const sub = this.workflowService.createDefinition(payload).subscribe((def) => {
      this.submitting = false;
      if (!def) {
        this.dialogService.customSnackBar("error", "red", "Failed to create workflow");
        return;
      }

      // Preserve the chosen trigger_type on the returned definition even if
      // the server response omits it — downstream navigation depends on it.
      if (!def.trigger_type) {
        (def as any).trigger_type = v.trigger_type;
      }

      this.dialogRef.close({ definition: def, triggerConfig });
    });
    this.reqSubcription.push(sub);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
